// API Security · Authorization — denial log item side drawer (v3 shell).

import React from "react";
import PropTypes from "prop-types";
import { Stack, Box, Typography, Chip } from "@mui/material";
import { ShieldX, Eye } from "lucide-react";
import copy from "copy-to-clipboard";
import colors from "../../../../../../utils/colors";
import LayerdIcon from "../../../../../common/LayerdIcon";
import { AsecMono } from "./asecPrimitives";
import { DETECTION_ICONS } from "../../../../../../icons/detectionLucideIcons";
import WafEventDrawerShell from "../../../detection/WafEventDrawerShell";
import FindingDrawerHeader from "../../../detection/v3/FindingDrawerHeader";
import SmartParseRawJsonViewer from "../../../detection/v3/SmartParseRawJsonViewer";
import {
  SmartParseSectionCard as SectionCard,
  DrawerFieldStack,
} from "../../../detection/v3/smartParseDrawerUi";
import {
  TEXT_PRIMARY,
  tagChipSx,
} from "../../../detection/wafEventDrawerShared";

function denialFullPath(row) {
  return `${row.path || ""}${row.hot || ""}${row.tail || ""}`;
}

function MismatchClaim({ a, b, op }) {
  const sign = op === "in" ? "∉" : op === "prefix" ? "≄" : "≠";
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: 12 }}>
      <AsecMono sx={{ fontSize: 12 }}>{a}</AsecMono>
      <Box component="span" sx={{ color: "#A11228", fontWeight: 700 }}>{sign}</Box>
      <Box component="span" sx={{
        color: colors.primary[300], backgroundColor: "rgba(99,113,247,0.10)",
        px: 0.75, borderRadius: "4px", fontWeight: 600, fontSize: 12,
      }}>{b}</Box>
    </Box>
  );
}

function VerdictChip({ verdict }) {
  const enforced = verdict === "deny";
  return (
    <Chip
      size="small"
      label={(
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <LayerdIcon icon={enforced ? ShieldX : Eye} size={12} color="inherit" />
          <span>{enforced ? "403 enforced" : "logged only"}</span>
        </Stack>
      )}
      sx={{
        ...tagChipSx,
        height: 26,
        backgroundColor: enforced ? "rgba(161, 18, 40, 0.10)" : "rgba(194, 144, 33, 0.12)",
        color: enforced ? "#A11228" : "#7a5b10",
        border: `1px solid ${enforced ? "rgba(161, 18, 40, 0.25)" : "rgba(194, 144, 33, 0.30)"}`,
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
}

export default function AuthzDenialDrawer({ open, onClose, row }) {
  const [snackOpen, setSnackOpen] = React.useState(false);

  const handleCopy = (value) => {
    if (!value || value === "—") return;
    copy(String(value));
    setSnackOpen(true);
    setTimeout(() => setSnackOpen(false), 2000);
  };

  if (!row) {
    return (
      <WafEventDrawerShell open={false} onClose={onClose} title="Authorization Denial">
        {null}
      </WafEventDrawerShell>
    );
  }

  const fullPath = denialFullPath(row);

  return (
    <>
      <WafEventDrawerShell
        open={open}
        onClose={onClose}
        title="Authorization Denial"
        headerVariant="hero"
        headerContent={(
          <FindingDrawerHeader
            embedded
            title="Authorization Denial"
            method={row.method}
            path={fullPath}
            detailLine={row.subject ? `Subject: ${row.subject}` : null}
            icon={ShieldX}
            idLabel="ID"
            idValue={row.id}
            onCopyId={handleCopy}
          />
        )}
      >
        <SectionCard icon={DETECTION_ICONS.detection} title="Denial summary">
          <DrawerFieldStack
            columns={2}
            fields={[
              {
                key: "verdict",
                label: "Verdict",
                content: <VerdictChip verdict={row.verdict} />,
              },
              {
                key: "timestamp",
                label: "Time",
                value: row.time,
              },
              row.since
                ? { key: "relative", label: "Relative", value: row.since }
                : null,
            ]}
          />
        </SectionCard>

        <SectionCard icon={DETECTION_ICONS.info} title="Ownership mismatch">
          <DrawerFieldStack
            columns={1}
            fields={[
              row.a && row.b
                ? {
                  key: "mismatch",
                  label: "Claim vs path value",
                  fullRow: true,
                  content: <MismatchClaim a={row.a} b={row.b} op={row.mismatchOp} />,
                }
                : null,
              row.mismatchOp
                ? { key: "operator", label: "Operator", value: row.mismatchOp }
                : null,
            ]}
          />
        </SectionCard>

        <SectionCard icon={DETECTION_ICONS.info} title="Raw data" defaultExpanded={false}>
          <SmartParseRawJsonViewer
            data={row}
            onCopy={handleCopy}
            filename="authz_denial.json"
          />
        </SectionCard>
      </WafEventDrawerShell>

      {snackOpen && (
        <Box
          sx={{
            position: "fixed",
            bottom: 80,
            right: 24,
            backgroundColor: TEXT_PRIMARY,
            color: "#fff",
            px: 2,
            py: 1,
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 1400,
          }}
        >
          <Typography sx={{ fontSize: 14, color: "#fff" }}>
            Copied to clipboard!
          </Typography>
        </Box>
      )}
    </>
  );
}

AuthzDenialDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  row: PropTypes.object,
};
